import React, { useState } from "react";
import classNames from "classnames";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Counterparty } from "../../../../common/types/counterparty";
import styles from "../RulesTable.module.scss";
import { TransactionRuleGroup } from "../../../../common/types/transactionRule";
import { EmptyState } from "./EmptyState";
import { RuleSortableRow } from "./RuleSortableRow";

/**
 * RulesGroupBody renders all rules for a group, or its empty state.
 */
interface RulesGroupBodyProps {
    readonly group: TransactionRuleGroup;
    readonly overId: string | null;
    readonly groupListeners: React.HTMLAttributes<HTMLElement>;
    readonly groupAttributes: React.HTMLAttributes<HTMLElement>;
    readonly isGroupDragged: boolean;
    readonly counterparties: Counterparty[];
    readonly transactionCounts: Record<string, number>;
    readonly collapsedGroupId?: string | null;
    readonly priority?: number;
}

export const RulesGroupBody: React.FC<RulesGroupBodyProps> = ({
    group,
    overId,
    groupListeners,
    groupAttributes,
    isGroupDragged,
    counterparties,
    transactionCounts,
    collapsedGroupId,
    priority,
}) => {
    const [isLocalCollapsed, setIsLocalCollapsed] = useState(false);

    // Group is collapsed if it's the one being dragged (collapsedGroupId === group.id)
    const isCollapsed = collapsedGroupId === group.id || isLocalCollapsed;

    return (
        <SortableContext
            key={group.id + "-rules"}
            items={group.rules?.map((r) => r.id) ?? []}
            strategy={verticalListSortingStrategy}
        >
            {group.rules?.length === 0 ? (
                <tr
                    className={styles.rowHover}
                    style={{ position: "relative" }}
                >
                    <EmptyState
                        group={group}
                        groupListeners={groupListeners}
                        groupAttributes={groupAttributes}
                        priority={priority}
                    />
                    {overId === group.id && isGroupDragged && (
                        <>
                            <div
                                className={classNames(
                                    styles.insertionIndicator,
                                    styles.top,
                                    styles.withDot,
                                )}
                            />
                            <div
                                className={classNames(
                                    styles.insertionIndicatorDot,
                                    styles.top,
                                )}
                            />
                        </>
                    )}
                </tr>
            ) : (
                group.rules?.map((rule, ruleIndex) => (
                    <RuleSortableRow
                        key={rule.id}
                        rule={rule}
                        ruleIndex={ruleIndex}
                        group={group}
                        overId={overId}
                        groupListeners={groupListeners}
                        groupAttributes={groupAttributes}
                        isGroupDragged={isGroupDragged}
                        counterparties={counterparties}
                        transactionCounts={transactionCounts}
                        isCollapsed={isCollapsed}
                        toggleCollapse={() =>
                            setIsLocalCollapsed(!isLocalCollapsed)
                        }
                        priority={priority}
                    />
                ))
            )}
        </SortableContext>
    );
};
